import {useState} from "react";

import {Card, CardContent} from "@/components/ui/card";
import {Loader} from "@/src/modules/core/components";
import {useStoredMe} from "@/src/modules/core/store/me";
import useHousehold from "@/src/modules/households/hooks/use-household";
import {cn} from "@/lib/utils";

import {
  useResidentHomePropertyAmenities,
} from "../hooks/use-resident-home-property-amenities";
import {
  getResidentFacilitiesRestrictionMessage,
} from "../lib/resident-amenity-restriction-copy";
import {
  RESIDENT_HOME_AMENITIES_PREVIEW_LIMIT,
  RESIDENT_HOME_BLOCK_TITLE_CLASS,
  RESIDENT_HOME_CARD_CONTENT_CLASS,
  RESIDENT_HOME_CARD_SHELL_CLASS,
  RESIDENT_HOME_PILLAR_CARD_CONTENT_CLASS,
  RESIDENT_HOME_PILLAR_FILLER_CLASS,
  RESIDENT_HOME_PILLAR_FOOTER_PLACEHOLDER_CLASS,
  RESIDENT_HOME_PILLAR_HEADER_ACTION_SLOT_CLASS,
  RESIDENT_HOME_PILLAR_HEADER_ROW_CLASS,
  RESIDENT_HOME_PILLAR_LIST_CLASS,
  RESIDENT_HOME_PILLAR_LIST_SLOT_CLASS,
  RESIDENT_HOME_TEXT_INSET_CLASS,
} from "../lib/resident-home-block-layout";
import {RESIDENT_HOME_AMENITIES_ICON} from "../lib/resident-home-teaser";
import {ResidentHomeAmenitiesPreviewRow} from "./resident-home-amenities-preview-row";
import {ResidentHomeAmenitiesSheet} from "./resident-home-amenities-sheet";
import {ResidentHomeCardPreviewFooter} from "./resident-home-card-preview-footer";
import {ResidentHomeIconTextBlock} from "./resident-home-icon-text-block";
import {ResidentAmenityRestrictionNotice} from "./resident-amenity-restriction-notice";

const PREVIEW_LIMIT = RESIDENT_HOME_AMENITIES_PREVIEW_LIMIT;

type ResidentHomeAmenitiesCardProps = {
  className?: string;
};

export function ResidentHomeAmenitiesCard({className}: ResidentHomeAmenitiesCardProps) {
  const [expandOpen, setExpandOpen] = useState(false);
  const me = useStoredMe();
  const householdId = me?.house_hold_id ?? null;
  const {household} = useHousehold(householdId);
  const facilitiesRestrictionMessage = getResidentFacilitiesRestrictionMessage(household);

  const {amenities, totalRecords, canShow, isPending, isError} = useResidentHomePropertyAmenities({
    limit: PREVIEW_LIMIT,
  });

  const showExpand = totalRecords > PREVIEW_LIMIT;

  return (
    <>
      <Card className={cn(RESIDENT_HOME_CARD_SHELL_CLASS, "flex h-full min-h-0 flex-col", className)}>
        <CardContent
          className={cn(
            RESIDENT_HOME_CARD_CONTENT_CLASS,
            RESIDENT_HOME_PILLAR_CARD_CONTENT_CLASS,
            "min-h-0 flex-1",
          )}
        >
          {facilitiesRestrictionMessage ? (
            <ResidentAmenityRestrictionNotice message={facilitiesRestrictionMessage} />
          ) : null}

          <div className={RESIDENT_HOME_PILLAR_HEADER_ROW_CLASS}>
            <ResidentHomeIconTextBlock
              icon={RESIDENT_HOME_AMENITIES_ICON.icon}
              iconWrapperClassName={RESIDENT_HOME_AMENITIES_ICON.iconWrapperClassName}
              iconClassName={RESIDENT_HOME_AMENITIES_ICON.iconClassName}
              className="min-w-0 flex-1 items-start"
              title={<p className={RESIDENT_HOME_BLOCK_TITLE_CLASS}>Pool & amenities</p>}
            />
            <span className={RESIDENT_HOME_PILLAR_HEADER_ACTION_SLOT_CLASS} aria-hidden>
              ·
            </span>
          </div>

          <div className={cn(RESIDENT_HOME_PILLAR_LIST_SLOT_CLASS, RESIDENT_HOME_TEXT_INSET_CLASS)}>
            {!canShow ? (
              <p className="text-muted-foreground text-sm">
                You do not have permission to view amenities.
              </p>
            ) : isPending ? (
              <Loader className="min-h-24" />
            ) : isError ? (
              <p className="text-muted-foreground text-sm">Unable to load amenities.</p>
            ) : amenities.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                No amenities configured for your property.
              </p>
            ) : (
              <div className={RESIDENT_HOME_PILLAR_LIST_CLASS}>
                {amenities.map((amenity) => (
                  <ResidentHomeAmenitiesPreviewRow
                    key={amenity.property_amenity_id}
                    amenity={amenity}
                    household={household}
                  />
                ))}
              </div>
            )}
          </div>

          <div className={RESIDENT_HOME_PILLAR_FILLER_CLASS} aria-hidden />

          {canShow && !isPending && !isError && amenities.length > 0 ? (
            <ResidentHomeCardPreviewFooter
              showingCount={amenities.length}
              totalRecords={totalRecords}
              onShowMore={showExpand ? () => setExpandOpen(true) : undefined}
            />
          ) : (
            <div aria-hidden className={RESIDENT_HOME_PILLAR_FOOTER_PLACEHOLDER_CLASS} />
          )}
        </CardContent>
      </Card>

      <ResidentHomeAmenitiesSheet
        open={expandOpen}
        onOpenChange={setExpandOpen}
        household={household}
      />
    </>
  );
}
